import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, BellOff, CheckCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationsContext";
import useClickOutside from "@/hooks/useClickOutside";
import { getPushState, enablePushAlerts, ensurePushSubscription, unlockAudio } from "@/lib/pushNotifications";

function timeAgo(iso, lang) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return lang === "en" ? "just now" : "à l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return lang === "en" ? `${m} min ago` : `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return lang === "en" ? `${h} h ago` : `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return lang === "en" ? "yesterday" : "hier";
  return lang === "en" ? `${d} d ago` : `il y a ${d} j`;
}

/** Header notification bell — unread badge + dropdown panel + push opt-in (unified alerts). */
export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { items, unread, markAllRead, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  // Position réelle du panneau sous la cloche (mesurée à l'ouverture) : fiable
  // partout, y compris dans l'app iOS où contentInset "always" rend env(safe-area) = 0
  // et où un top fixe recouvrait le header (taps bloqués).
  const [panelTop, setPanelTop] = useState(null);
  const [pushState, setPushState] = useState("unsupported");
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const wrapRef = useRef(null);
  // Any click/tap outside the bell + panel closes the menu
  useClickOutside(wrapRef, () => setOpen(false), open);

  useEffect(() => {
    if (!user) return;
    getPushState().then((s) => {
      setPushState(s);
      // Self-healing: permission granted → silently re-sync the subscription
      if (s === "subscribed" || (s === "idle" && typeof Notification !== "undefined" && Notification.permission === "granted")) {
        ensurePushSubscription().then((ok) => { if (ok) setPushState("subscribed"); });
      }
    });
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  const enablePush = async () => {
    unlockAudio();
    try {
      await enablePushAlerts();
      setPushState("subscribed");
      toast.success(t("notif.pushEnabled"));
    } catch (e) {
      setPushState(typeof Notification !== "undefined" && Notification.permission === "denied" ? "denied" : "idle");
      toast.error(e?.message || t("notif.pushError"));
    }
  };

  const onItemClick = (n) => {
    if (!n.read) markRead(n.notification_id);
    setOpen(false);
    navigate(n.url || "/");
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => {
          if (!open && wrapRef.current && window.innerWidth < 768) {
            // S'ancrer sous le BAS du header entier (jamais de recouvrement,
            // quelle que soit la safe-area / l'inset natif iOS)
            const header = wrapRef.current.closest("header");
            const base = (header || wrapRef.current).getBoundingClientRect().bottom;
            setPanelTop(Math.round(base + 8));
          } else {
            setPanelTop(null);
          }
          setOpen((v) => !v);
        }}
        data-testid="notif-bell"
        aria-label="Notifications"
        className="relative rounded-full bg-elevated hover:bg-[#EBE8E0] p-2"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 bg-brand-red text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center font-semibold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} data-testid="notif-panel" style={panelTop != null ? { top: panelTop } : undefined} className="fixed inset-x-3 top-[80px] mx-auto max-w-[380px] max-h-[70vh] flex flex-col md:absolute md:inset-x-auto md:right-0 md:top-auto md:mt-2 md:w-[380px] bg-white rounded-2xl shadow-xl border border-sand z-50 overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-sand shrink-0">
              <div className="font-display font-semibold text-sm">{t("notif.title")}</div>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button onClick={markAllRead} data-testid="notif-mark-all" className="inline-flex items-center gap-1 text-xs font-semibold text-brand-red hover:underline">
                    <CheckCheck className="w-3.5 h-3.5" /> {t("notif.markAll")}
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  data-testid="notif-close"
                  aria-label={t("notif.close")}
                  className="rounded-full bg-elevated hover:bg-[#EBE8E0] p-1.5 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {pushState !== "unsupported" && pushState !== "subscribed" && (
              <button
                onClick={enablePush}
                disabled={pushState === "denied"}
                data-testid="notif-push-toggle"
                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold bg-brand-yellow/15 hover:bg-brand-yellow/25 disabled:opacity-60 text-ink border-b border-sand"
              >
                {pushState === "denied" ? <BellOff className="w-3.5 h-3.5" /> : <BellRing className="w-3.5 h-3.5 text-brand-red" />}
                {pushState === "denied" ? t("notif.pushDenied") : t("notif.pushCta")}
              </button>
            )}

            <div className="flex-1 min-h-0 max-h-[60vh] overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-mute" data-testid="notif-empty">
                  <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                  {t("notif.empty")}
                </div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.notification_id}
                    onClick={() => onItemClick(n)}
                    data-testid={`notif-item-${n.notification_id}`}
                    className={`w-full text-left px-4 py-3 border-b border-sand/60 last:border-0 hover:bg-elevated transition-colors ${!n.read ? "bg-brand-yellow/10" : "bg-white"}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-brand-red shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm truncate ${!n.read ? "font-semibold text-ink" : "font-medium text-ink/80"}`}>{n.title}</div>
                        {n.body && <div className="text-xs text-mute mt-0.5 line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] text-mute mt-1">{timeAgo(n.created_at, i18n.language)}</div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
      )}
    </div>
  );
}
